import { useState } from "react";
import { Alert, Box, Button, CircularProgress, Switch } from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import AddIcon from "@mui/icons-material/Add";
import {
  TextField,
  NumberField,
  FunctionField,
  ListContextProvider,
  useListController,
  useNotify,
  useRefresh,
  useRecordContext,
} from "react-admin";
import { useDefineAppLocation } from "@react-admin/ra-navigation";
import { ResourceName, TaskTemplate, AlertType } from "@/types";
import { StatusChip } from "@/components/common/fields/StatusChipField";
import { ColorType } from "@/types/shared/uiComponents";
import List from "@/components/ui/react-admin/styledList/StyledList";
import { PageHeader } from "@/components/common/PageHeader";
import { StyledDatagrid } from "@/components/ui/react-admin/StyledDatagrid";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { httpClient, getPartnerAwareApiUrl } from "@/providers/dataProvider/dataProvider";
import { CreateInDialog } from "@/components/common/CreateInDialog";
import { TaskTemplateListAside } from "./TaskTemplateListAside";
import { TaskTemplateDrawer, TASK_TEMPLATE_DRAWER_WIDTH } from "./components/TaskTemplateDrawer";

const ActiveToggle = () => {
  const record = useRecordContext<TaskTemplate>();
  const { canEdit } = useResourcePermissions(ResourceName.TASK_TEMPLATES);
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const refresh = useRefresh();
  const [isUpdating, setIsUpdating] = useState(false);

  if (!record) return null;

  const handleToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!canEdit) return;

    setIsUpdating(true);
    const action = record.is_active ? "deactivate" : "activate";
    const apiUrl = getPartnerAwareApiUrl();

    try {
      await httpClient(`${apiUrl}/${ResourceName.TASK_TEMPLATES}/${record.id}/${action}`, { method: "POST" });
      notify(`Template ${record.is_active ? "deactivated" : "activated"}`, { type: AlertType.SUCCESS });
      refresh();
    } catch (error) {
      notifyError(error);
    } finally {
      setIsUpdating(false);
    }
  };

  if (isUpdating) {
    return <CircularProgress size={20} />;
  }

  return <Switch checked={!!record.is_active} onClick={handleToggle} disabled={!canEdit} size="small" />;
};

export const TaskTemplateList = () => {
  const listContext = useListController({
    resource: ResourceName.TASK_TEMPLATES,
    sort: { field: "rough_order", order: "ASC" },
  });
  const { isPending: loading } = listContext;
  const { canCreate } = useResourcePermissions(ResourceName.TASK_TEMPLATES);
  const refresh = useRefresh();

  useDefineAppLocation("admin.task-templates");

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [openCreate, setOpenCreate] = useState(false);

  const drawerOpen = !!selectedTemplateId;

  return (
    <ListContextProvider value={listContext}>
      <Box
        sx={{
          transition: "margin-right 0.3s ease",
          marginRight: drawerOpen ? `${TASK_TEMPLATE_DRAWER_WIDTH}px` : 0,
        }}
      >
        <PageHeader
          customTitle="Global Task Templates"
          headerIcon={<TuneIcon sx={{ fontSize: 28, color: "primary.main" }} />}
          rightContent={
            canCreate ? (
              <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => setOpenCreate(true)}>
                Create Template
              </Button>
            ) : undefined
          }
        />
        <Alert severity="info" sx={{ mx: 2, mb: 2 }}>
          These are global task templates that apply across all partners. To configure task settings for a specific
          partner, go to their Partner page.
        </Alert>
        <List
          perPage={25}
          resource={ResourceName.TASK_TEMPLATES}
          actions={false}
          aside={<TaskTemplateListAside />}
          disableSyncWithLocation
          loading={loading}
        >
          <StyledDatagrid
            bulkActionButtons={false}
            rowClick={(id) => {
              setSelectedTemplateId(String(id));
              return false;
            }}
            rowSx={(record: TaskTemplate) => ({
              backgroundColor: record.id === selectedTemplateId ? "action.selected" : undefined,
              borderLeft: record.id === selectedTemplateId ? "3px solid" : "3px solid transparent",
              borderLeftColor: record.id === selectedTemplateId ? "primary.main" : "transparent",
              cursor: "pointer",
            })}
          >
            <TextField source="title" label="Title" />
            <FunctionField<TaskTemplate>
              label="Category"
              render={(record) =>
                record?.template_category ? (
                  <StatusChip label={formatToTitleCase(record.template_category)} color={ColorType.Info} />
                ) : (
                  "—"
                )
              }
            />
            <FunctionField<TaskTemplate>
              label="Trigger"
              render={(record) => (record?.trigger_event ? formatToTitleCase(record.trigger_event) : "—")}
            />
            <FunctionField<TaskTemplate>
              label="Subject"
              render={(record) => (record?.record_subject ? formatToTitleCase(record.record_subject) : "—")}
            />
            <NumberField source="priority" label="Priority" />
            <NumberField source="default_due_days" label="Due Days" />
            <FunctionField<TaskTemplate> label="Active" render={() => <ActiveToggle />} />
          </StyledDatagrid>
        </List>
      </Box>

      {/* Template detail drawer */}
      <TaskTemplateDrawer
        templateId={selectedTemplateId}
        onClose={() => setSelectedTemplateId(null)}
        onDeleted={() => {
          setSelectedTemplateId(null);
          refresh();
        }}
      />

      {/* Create template dialog */}
      <CreateInDialog open={openCreate} onClose={() => setOpenCreate(false)} resource={ResourceName.TASK_TEMPLATES} />
    </ListContextProvider>
  );
};
